import { refreshAccessToken } from "@website/hooks/auth";
import { MaterialIcon } from "@website/icons/material";
import { Button } from "@website/ui/button";
import { InfoChips } from "@website/ui/info-chips";
import { ListItem } from "@website/ui/list-item";
import { Popover } from "@website/ui/popover";
import { getChannel } from "@website/utils/channel/get-channel";
import { isChannel } from "@website/utils/channel/helpers";
import { numberConvert } from "@website/utils/number-convert";
import { getYaldaLogo } from "@website/utils/special-dates-logo";
import { logoSchema } from "@website/widget-v2/header/logo/schema";
import { PurchaseMenu } from "@website/widget/common/components/purchase-menu";
import { usePurchaseMenu } from "@website/widget/common/utils/use-purchase-menu";
import { Drawer } from "@website/widget/header/components";
import { HeaderLogin } from "@website/widget/header/header-login/header-login";
import styles from "@website/widget/header/header.module.scss";
import { HeaderPropertiesType } from "@website/widget/header/header.types";
import {
  useDrawer,
  useServices,
  useSupportMenu
} from "@website/widget/header/hooks";
import { LoginModal } from "@website/widget/login";
import RedirectModal from "@website/widget/redirect-modal";
import { FC, memo, useEffect } from "react";

const Header: FC<HeaderPropertiesType> = (properties) => {
  const {
    customTitleText,
    showMinimalLogo = false,
    showAuth = !isChannel(["jek", "b2b", "SNAPP_AD", "ads", "snapp-ir"]),
    minimalView = !isChannel(["website", "web", "ads", "shab"]),
    showBackButton = !isChannel(["website", "web", "ads", "telesale", "shab"]),
    showMenuButton = !isChannel(["snapp-ir", "b2b", "ads", "SNAPP_AD"]),
    showDropShadow = true,
    showBottomBorder = false,
    state = "normal",
    showTrackPurchase = !isChannel([
      "ads",
      "SNAPP_AD",
      "snapp-ir",
      "b2b",
      "shab"
    ]),
    showAgencies = !isChannel(["snapp-ir", "b2b", "ads", "SNAPP_AD", "shab"]),
    showTravelServices = !isChannel(["snapp-ir", "b2b", "SNAPP_AD", "ads"]),
    // showDownloadApp = !isChannel([
    //   "snapp-ir",
    //   "b2b",
    //   "ads",
    //   "SNAPP_AD",
    //   "ads"
    // ]),
    showSupportAgent = !isChannel(["b2b", "shab"])
  } = properties;

  useEffect(() => refreshAccessToken(), []);

  const { onSupportMenuOpen, onSupportMenuClose, supportMenuIsOpen } =
    useSupportMenu();
  const { drawerIsOpen, onDrawerOpen, onDrawerClose } = useDrawer();

  const { services, onGoBack } = useServices();

  const {
    purchaseList,
    purchaseMenuIsOpen,
    onPurchaseMenuClose,
    onPurchaseMenuOpen
  } = usePurchaseMenu();

  const channel = getChannel();
  const yaldaLogoInfo = getYaldaLogo(channel);
  const { label, image, width, height, title } =
    yaldaLogoInfo ||
    (showMinimalLogo
      ? logoSchema.minimal
      : logoSchema[getChannel() as keyof typeof logoSchema] ||
        logoSchema.default);

  return (
    <div
      className={`${styles["container"]} ${
        showDropShadow ? "" : styles["without-shadow"]
      } ${showBottomBorder ? styles["with-bottom-border"] : ""} ${
        styles[state]
      } ${properties.className || ""}`}
    >
      <div className={styles["middle"]}>
        <nav className="flex h-full items-center gap-2 lg:gap-4">
          {showMenuButton && !minimalView && (
            <Button
              EndIcon={<MaterialIcon name="menu" />}
              aria-label="open menu"
              className={styles["menu-button"]}
              color="neutral"
              equalDimension
              onClick={onDrawerOpen}
              size="small"
              variant="text"
            />
          )}

          {showBackButton && (
            <Button
              EndIcon={<MaterialIcon name="arrow_forward" variant="outlined" />}
              aria-label="close"
              className={styles["back-button"]}
              color="neutral"
              equalDimension
              onClick={onGoBack}
              size="small"
              variant="text"
            />
          )}

          <a aria-label={label} href="/">
            <img
              alt={`${label} logo`}
              className={styles["logo"]}
              height={height || 78}
              loading="lazy"
              src={(image as unknown as { src: string }).src as string}
              title={title}
              width={width || 137}
            />
          </a>

          {customTitleText && (
            <>
              <div className={styles["custom-title-separator"]} />
              <div className={styles["custom-title-text"]}>
                {customTitleText}
              </div>
            </>
          )}

          {!minimalView && (
            <>
              <div className={styles["services-wrapper"]}>
                {showTravelServices && (
                  <Button
                    EndIcon={<MaterialIcon name="keyboard_arrow_down" />}
                    aria-label="خدمات مسافرتی"
                    color="neutral"
                    size="medium"
                    variant="text"
                  >
                    خدمات مسافرتی
                  </Button>
                )}
                <div className={styles["service-menu-container"]}>
                  <div className={styles["service-menu"]}>
                    <div className={styles["menu"]}>
                      {services?.map((item) => (
                        <a
                          className={styles["menu-item"]}
                          href={item.url}
                          key={item.title}
                        >
                          <span className={styles["menu-item-icon"]}>
                            {item.icon}
                          </span>
                          <div className={styles["menu-item-title"]}>
                            {item.title}
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              {showAgencies && (
                <Button
                  className={styles["agencies-button"]}
                  color="neutral"
                  href="https://b2b.snapptrip.com/b2b-login"
                  role="link"
                  size="medium"
                  target="_blank"
                  variant="text"
                >
                  آژانس ها و شرکت ها
                </Button>
              )}
            </>
          )}
        </nav>

        <div className={styles["divider"]} />
        {!minimalView && (
          <>
            {/* {showDownloadApp && (
              <Button
                className={styles["download-app"]}
                color="neutral"
                href={`/app`}
                role="link"
                size="medium"
                target="_blank"
                variant="text"
              >
                {"دانلود اپلیکیشن"}
              </Button>
            )} */}

            {showTrackPurchase && (
              <div className="relative">
                <InfoChips
                  size="compact"
                  variant="round-contained"
                  text="سفرهای من"
                  className="!absolute -right-10 -top-0 !w-max lg:!hidden"
                />
                <Button
                  className={styles["track-order"]}
                  color="neutral"
                  onClick={onPurchaseMenuOpen}
                  size="medium"
                  variant="text"
                >
                  سفرهای من
                </Button>
                <Button
                  EndIcon={<MaterialIcon name="luggage" variant="outlined" />}
                  aria-label="Show Purchase List"
                  className={styles["track-order-small"]}
                  color="neutral"
                  equalDimension
                  onClick={onPurchaseMenuOpen}
                  variant="text"
                />
                <PurchaseMenu
                  isOpen={purchaseMenuIsOpen}
                  list={purchaseList}
                  onClose={onPurchaseMenuClose}
                />
              </div>
            )}
          </>
        )}
        <div className={styles["support-wrapper"]}>
          {showSupportAgent && (
            <Button
              EndIcon={<MaterialIcon name="support_agent" />}
              aria-label="نمایش لینک های پشتیبانی"
              color="neutral"
              equalDimension
              onClick={onSupportMenuOpen}
              role="button"
              size="medium"
              variant="text"
              className="!mx-0"
            />
          )}

          <Popover
            gap="16px"
            horizontalPosition="end"
            onClose={onSupportMenuClose}
            open={supportMenuIsOpen}
          >
            <div className={styles["support-menu"]}>
              <ListItem
                StartIcon={<MaterialIcon name="support_agent" />}
                aria-label="تماس با پشتیبانی"
                href="tel:+982192000123"
                role="link"
                subtitle={
                  <div className={styles["support-menu-phone-number"]}>
                    {numberConvert("+98 21 92000123", {
                      locale: "fa"
                    })}
                  </div>
                }
                title="تماس با پشتیبانی"
              />
            </div>
          </Popover>
        </div>

        {showAuth && !isChannel(["ads", "SNAPP_AD", "snapp-ir"]) ? (
          <HeaderLogin />
        ) : null}
      </div>
      <Drawer onClose={onDrawerClose} open={drawerIsOpen} services={services} />
      <LoginModal />
      {isChannel(["shab"]) && <RedirectModal />}
    </div>
  );
};

const HeaderMemoized = memo(Header);
export { HeaderMemoized as Header };
